import { OrgPicker } from '@/components/OrgPicker/OrgPicker'
import { YesNoField } from '@/features/SalesforceIngest/components/YesNoField'
import type {
  ResolutionDecision,
  ResolutionDescription,
} from '@/features/SalesforceIngest/lib/describeResolution'
import { Box, Text } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

type Props = {
  decision: ResolutionDecision
  onChange: (patch: Partial<ResolutionDecision>) => void
  isPrefilled: boolean
  prefillKey: number
  orgIdError?: string
  description: ResolutionDescription | null
}

export function ResolveDecisionFields({
  decision,
  onChange,
  isPrefilled,
  prefillKey,
  orgIdError,
  description,
}: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  return (
    <>
      {isPrefilled ? (
        <Text fontSize="sm" color="text.text-subtle">
          {String(t('salesforceIngest.form.prefilled'))}
        </Text>
      ) : null}

      <YesNoField
        label={String(t('salesforceIngest.form.createNewOrg.label'))}
        helper={String(t('salesforceIngest.form.createNewOrg.helper'))}
        value={decision.createNewOrg}
        onChange={(createNewOrg) => onChange({ createNewOrg })}
      />

      <YesNoField
        label={String(t('salesforceIngest.form.createNewAccount.label'))}
        helper={String(t('salesforceIngest.form.createNewAccount.helper'))}
        value={decision.createNewAccount}
        onChange={(createNewAccount) => onChange({ createNewAccount })}
      />

      {decision.createNewOrg ? null : (
        <OrgPicker
          key={prefillKey}
          value={decision.orgId}
          onChange={(orgId) => onChange({ orgId })}
          label={String(t('salesforceIngest.form.orgId.label'))}
          helperText={String(t('salesforceIngest.form.orgId.helper'))}
          required
          error={
            orgIdError
              ? String(t('salesforceIngest.form.orgId.error.required'))
              : undefined
          }
        />
      )}

      {description ? (
        <Box
          borderLeftWidth="3px"
          borderColor="border.emphasized"
          pl={4}
          py={1}
        >
          <Text fontWeight="medium" mb={1}>
            {String(t('salesforceIngest.form.thisWill'))}
          </Text>
          <Text color="text.text-subtle">
            {String(
              t(
                `salesforceIngest.case.${description.caseKey}`,
                description.values,
              ),
            )}
          </Text>
        </Box>
      ) : null}
    </>
  )
}
